import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Crown, CalendarDays, ChevronLeft, ChevronRight, Flame, Users, Lock } from "lucide-react";
import { MatMapDialog, prettyDate, isHeated, shortDate } from "./MatMapDialog";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const Schedule = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [active, setActive] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [friends, setFriends] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStartIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d.toLocaleDateString("en-CA");
  })();

  const loadClasses = useCallback(async () => {
    try {
      if (selectedDate) {
        const { data } = await api.get(`/classes?date=${selectedDate}`);
        setClasses(data);
      } else if (weekOffset > 0) {
        // later weeks: one call returns each class dated for that week
        const { data } = await api.get(`/classes/week?start=${weekStartIso}`);
        setClasses(Object.values(data).flat());
      } else {
        const { data } = await api.get("/classes");
        setClasses(data);
      }
    } catch { /* silent */ }
  }, [selectedDate, weekOffset, weekStartIso]);

  const loadFriends = useCallback(async () => {
    if (!user) return setFriends(null);
    try {
      const { data } = await api.get(selectedDate ? `/classes/friends?date=${selectedDate}` : "/classes/friends");
      setFriends(data);
    } catch { setFriends(null); }
  }, [selectedDate, user]);

  useEffect(() => { loadFriends(); }, [loadFriends]);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  // a tab left open overnight picks up the new day / booking window when the client comes back to it
  useEffect(() => {
    const onWake = () => { if (document.visibilityState === "visible") loadClasses(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => { document.removeEventListener("visibilitychange", onWake); window.removeEventListener("focus", onWake); };
  }, [loadClasses]);

  const classEnded = useCallback((c) => {
    const d = selectedDate || c.date;
    if (!d) return false;
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((c.time || "").trim());
    if (!m) return false;
    let h = parseInt(m[1], 10) % 12;
    if (/pm/i.test(m[3])) h += 12;
    return new Date(`${d}T${String(h).padStart(2, "0")}:${m[2]}:00`) <= new Date();
  }, [selectedDate]);

  const openBooking = (cls, dateOverride) => {
    if (!user) { navigate("/login"); return; }
    setActiveDate(dateOverride ?? selectedDate ?? (weekOffset > 0 ? cls.date : null));
    setActive(cls);
  };

  // allow other components (e.g. coach dialog) to open the booking modal
  const openBookingRef = useRef(openBooking);
  useEffect(() => { openBookingRef.current = openBooking; });
  const classesRef = useRef(classes);
  useEffect(() => { classesRef.current = classes; }, [classes]);
  useEffect(() => {
    const h = (e) => {
      const c = classesRef.current.find((x) => x.class_id === e.detail?.class_id);
      if (c) openBookingRef.current(c);
    };
    window.addEventListener("sculpt:open-booking", h);
    return () => window.removeEventListener("sculpt:open-booking", h);
  }, []);

  // deep link (sessionStorage sculpt:open-mat): open a specific class + date mat map immediately
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  const tryDeepLink = useCallback(() => {
    if (!user) return;
    const raw = sessionStorage.getItem("sculpt:open-mat");
    if (!raw || !classesRef.current.length) return;
    try {
      const { class_id, date } = JSON.parse(raw);
      const c = classesRef.current.find((x) => x.class_id === class_id);
      if (!c) {
        if (date && date !== selectedDateRef.current) { selectedDateRef.current = date; setSelectedDate(date); return; }
        sessionStorage.removeItem("sculpt:open-mat");
        return;
      }
      sessionStorage.removeItem("sculpt:open-mat");
      openBookingRef.current(c, date);
      if (date) setSelectedDate(date);
      document.getElementById("schedule")?.scrollIntoView({ behavior: "auto" });
    } catch { sessionStorage.removeItem("sculpt:open-mat"); }
  }, [user]);
  useEffect(() => { tryDeepLink(); }, [classes, tryDeepLink]);
  useEffect(() => {
    window.addEventListener("sculpt:open-mat", tryDeepLink);
    return () => window.removeEventListener("sculpt:open-mat", tryDeepLink);
  }, [tryDeepLink]);

  const joinWaitlist = async (cls) => {
    if (!user) { navigate("/login"); return; }
    try {
      const d = selectedDate || (weekOffset > 0 ? cls.date : null);
      const { data } = await api.post("/waitlist", { class_id: cls.class_id, ...(d ? { date: d } : {}) });
      toast.success(`You're #${data.position} on the ${cls.class_name} waitlist. We'll ping you when a mat opens.`);
      loadClasses();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const crashClass = async (cls) => {
    if (!user) { navigate("/login"); return; }
    try {
      const d = selectedDate || (weekOffset > 0 ? cls.date : null);
      const { data } = await api.post("/bookings/crash", { class_id: cls.class_id, ...(d ? { date: d } : {}) });
      toast.success(`VIP crash confirmed — you're in ${cls.class_name}${d ? ` on ${shortDate(d)}` : ""} (overflow spot ${data.spot})!`);
      loadClasses();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const isVip = user && (user.tier === "vip" || user.role === "admin" || user.is_instructor);
  const isExempt = user && (user.role === "admin" || user.is_instructor);
  const bookingThrough = classes[0]?.booking_through;
  const nextMonthOpens = classes[0]?.next_month_opens;

  const grouped = DAY_ORDER.map((day) => ({
    day,
    theme: classes.find((c) => c.day === day)?.day_theme || "",
    items: classes.filter((c) => c.day === day),
  })).filter((g) => g.items.length)
    .sort((a, b) => (weekOffset > 0 ? (a.items[0]?.date || "").localeCompare(b.items[0]?.date || "") : 0));

  const MAX_WEEK_OFFSET = 16; // ~112 days, matches the 120-day calendar cap
  const todayIso = new Date().toLocaleDateString("en-CA");
  const dateStrip = (() => {
    const out = [];
    for (let i = 0; out.length < 12; i++) {
      const d = new Date();
      d.setDate(d.getDate() + weekOffset * 7 + i);
      const iso = d.toLocaleDateString("en-CA");
      out.push({
        iso,
        wd: d.toLocaleDateString("en-US", { weekday: "short" }),
        num: d.getDate(),
        today: iso === todayIso,
      });
    }
    return out;
  })();

  const stripLabel = (() => {
    const first = new Date(`${dateStrip[0].iso}T12:00:00`);
    const last = new Date(`${dateStrip[dateStrip.length - 1].iso}T12:00:00`);
    const m1 = first.toLocaleDateString("en-US", { month: "long" });
    const m2 = last.toLocaleDateString("en-US", { month: "long" });
    if (m1 === m2) return `${m1} ${first.getFullYear()}`;
    if (first.getFullYear() === last.getFullYear()) return `${m1} – ${m2} ${first.getFullYear()}`;
    return `${m1} ${first.getFullYear()} – ${m2} ${last.getFullYear()}`;
  })();

  return (
    <section id="schedule" data-testid="schedule" className="relative bg-black py-24 sm:py-32 border-t border-neutral-900">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-neon mb-5">Class Schedule</p>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              Book your <span className="text-stroke-neon">mat</span>
            </h2>
          </div>
          <p className="max-w-sm text-base text-neutral-400 font-light">
            Mats in rows of 8, with the instructor centered up front — pick the exact spot you want.
            Book any class <span className="text-neon">through {bookingThrough ? <span className="text-white">{prettyDate(bookingThrough)}</span> : "the end of this month"}</span> — next month's
            classes open on the <span className="text-white">last day of the month</span>.
          </p>
        </div>

        <div className="mb-3 flex items-center justify-between gap-3">
          <p data-testid="strip-month-label" aria-live="polite"
            className="font-display uppercase text-2xl sm:text-3xl leading-none text-white">
            {stripLabel}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
              disabled={weekOffset === 0} data-testid="prev-week-btn" aria-label="Previous week"
              className="inline-flex items-center gap-2 h-11 px-4 border-2 border-neutral-600 text-white text-[11px] uppercase tracking-[0.15em] font-semibold hover:border-neon hover:text-neon transition-colors disabled:opacity-30 disabled:hover:border-neutral-600 disabled:hover:text-white">
              <ChevronLeft size={16} aria-hidden="true" /> Previous week
            </button>
            <button type="button" onClick={() => setWeekOffset((o) => Math.min(MAX_WEEK_OFFSET, o + 1))}
              disabled={weekOffset >= MAX_WEEK_OFFSET} data-testid="next-week-btn" aria-label="Next week"
              className="inline-flex items-center gap-2 h-11 px-4 bg-neon border-2 border-neon text-black text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-white hover:border-white transition-colors disabled:opacity-30">
              Next week <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="mb-10 -mx-1 overflow-x-auto pb-2" role="group" aria-label="Pick a class date">
          <div className="flex gap-1 min-w-max px-1">
            <button type="button" onClick={() => { setSelectedDate(null); setWeekOffset(0); }} data-testid="date-week-btn"
              aria-pressed={!selectedDate}
              className={`shrink-0 px-4 py-3 text-[11px] uppercase tracking-[0.2em] border transition-colors
                ${!selectedDate ? "border-neon bg-neon text-black font-semibold" : "border-neutral-800 text-neutral-300 hover:border-neutral-500"}`}>
              {weekOffset === 0 ? "This Week" : `Week of ${shortDate(weekStartIso)}`}
            </button>
            {dateStrip.map((d) => (
              <button key={d.iso} type="button" onClick={() => setSelectedDate(d.iso)} data-testid={`date-btn-${d.iso}`}
                aria-pressed={selectedDate === d.iso}
                aria-label={`Show classes on ${prettyDate(d.iso)}`}
                className={`shrink-0 flex flex-col items-center px-3.5 py-2 border transition-colors
                  ${selectedDate === d.iso ? "border-neon bg-neon text-black font-semibold" : "border-neutral-800 text-neutral-300 hover:border-neutral-500"}`}>
                <span className="text-[10px] uppercase tracking-wider">{d.today ? "Today" : d.wd}</span>
                <span className="text-lg font-display leading-tight">{d.num}</span>
              </button>
            ))}
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="date-calendar-btn"
                  aria-label="Open calendar to pick any future date"
                  className={`shrink-0 inline-flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.2em] border transition-colors
                    ${selectedDate && !dateStrip.some((d) => d.iso === selectedDate) ? "border-neon bg-neon text-black font-semibold" : "border-neutral-800 text-neutral-300 hover:border-neon hover:text-neon"}`}>
                  <CalendarDays size={14} /> More dates
                </button>
              </PopoverTrigger>
              <PopoverContent data-testid="date-calendar-popover" className="w-auto p-0 bg-[#0A0A0A] border-neutral-800 text-white" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate ? new Date(`${selectedDate}T12:00:00`) : undefined}
                  onSelect={(d) => {
                    if (!d) return;
                    setSelectedDate(d.toLocaleDateString("en-CA"));
                    setCalOpen(false);
                  }}
                  disabled={(d) => {
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const max = new Date(today); max.setDate(max.getDate() + 120);
                    return d < today || d > max;
                  }}
                  initialFocus
                />
                <p className="px-3 pb-3 text-[11px] text-neutral-400">
                  Browse any future date — booking is open through the end of this month{nextMonthOpens ? `; ${prettyDate(nextMonthOpens).replace(/^\w+, /, "")} opens the month after` : ""}.
                </p>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {selectedDate && (
          <p data-testid="selected-date-label" className="text-sm text-neutral-300 mb-8 -mt-4">
            Showing classes for <span className="text-neon">{prettyDate(selectedDate)}</span>
            {classes.length === 0 ? " — no classes scheduled this day."
              : isExempt || !bookingThrough || selectedDate <= bookingThrough ? "."
              : ` — booking for this date opens ${prettyDate(nextMonthOpens)}.`}
          </p>
        )}

        {friends && !friends.enabled && (
          <button type="button" onClick={() => navigate("/account")} data-testid="friends-hint"
            className="mb-8 -mt-2 inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-neon transition-colors text-left">
            <Users size={12} aria-hidden="true" className="shrink-0" />
            {friends.reason === "restricted"
              ? (friends.account_status === "dormant"
                ? "Classmate names are hidden while your account is dormant — book & attend a class to restore them"
                : "Classmate names unlock once you've attended a class")
              : "Turn on name sharing to see who's coming to each class"}
          </button>
        )}

        <div className="flex flex-col gap-14">
          {grouped.map((g, gi) => (
            <motion.div
              key={g.day}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: gi * 0.03 }}
              data-testid={`day-${g.day.toLowerCase()}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-5 border-b border-neutral-900 pb-3">
                <h3 className="font-display uppercase text-3xl sm:text-4xl">{g.day}</h3>
                {(selectedDate || g.items[0]?.date) && (
                  <span data-testid={`day-date-${g.day.toLowerCase()}`} className="text-xs sm:text-sm text-neutral-400 tabular-nums">
                    {shortDate(selectedDate || g.items[0].date)}
                  </span>
                )}
                <span className="text-xs uppercase tracking-[0.25em] text-neon">{g.theme}</span>
                {isHeated(g.day) && (
                  <span data-testid={`heated-day-${g.day.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-orange-400 border border-orange-400/40 px-2.5 py-1">
                    <Flame size={11} aria-hidden="true" /> Heated — free with Hot membership
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                {g.items.map((c) => (
                  <div
                    key={c.class_id}
                    data-testid={`class-card-${c.class_id}`}
                    className="group border border-neutral-900 p-5 hover:bg-[#0A0A0A] transition-colors flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display text-xl text-white">{c.time}</span>
                        {(selectedDate || c.date) && (
                          <span data-testid={`class-date-${c.class_id}`} className="text-xs text-neutral-400 tabular-nums">
                            {shortDate(selectedDate || c.date)}
                          </span>
                        )}
                        {isHeated(c.day) && (
                          <span data-testid={`heated-${c.class_id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border text-orange-400 border-orange-400/40">
                            <Flame size={10} aria-hidden="true" /> Heated
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-neutral-300 truncate">{c.class_name}</p>
                      <p className="text-xs text-neutral-400">with {c.instructor} · {c.spots_left}/{c.spots || 16} open{c.waitlist_count > 0 ? ` · ${c.waitlist_count} waiting` : ""}{c.crashers > 0 ? ` · ${c.crashers} VIP crashing` : ""}</p>
                      {friends?.enabled && (friends.counts[c.class_id] || 0) > 0 && (
                        <p data-testid={`friends-count-${c.class_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-neon">
                          <Users size={11} aria-hidden="true" /> {friends.counts[c.class_id]} {friends.counts[c.class_id] === 1 ? "classmate" : "classmates"} sharing their name
                        </p>
                      )}
                    </div>
                    {c.cancelled ? (
                      <span data-testid={`cancelled-${c.class_id}`} className="shrink-0 text-[11px] uppercase tracking-[0.15em] text-neutral-400 border border-neutral-800 px-4 py-2.5">
                        Cancelled
                      </span>
                    ) : classEnded(c) ? (
                      <span data-testid={`ended-${c.class_id}`} className="shrink-0 text-[11px] uppercase tracking-[0.15em] text-neutral-500 border border-neutral-800 px-4 py-2.5">
                        Ended
                      </span>
                    ) : !c.general_open && !isExempt ? (
                      <span data-testid={`locked-${c.class_id}`} title={`Booking for ${c.date} opens ${prettyDate(c.next_month_opens)} at 12:00 AM Pacific`}
                        className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-neutral-400 border border-neutral-800 px-4 py-2.5 cursor-not-allowed">
                        <Lock size={11} aria-hidden="true" /> Opens {shortDate(c.next_month_opens)}
                      </span>
                    ) : c.spots_left === 0 ? (
                      isVip && c.crashers < (c.crash_cap ?? 4) ? (
                        <button
                          onClick={() => crashClass(c)}
                          data-testid={`crash-btn-${c.class_id}`}
                          className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] bg-neon text-black font-semibold px-4 py-2.5 hover:bg-white hover:text-black transition-colors"
                        >
                          <Crown size={12} /> Crash
                        </button>
                      ) : (
                        <div className="shrink-0 flex flex-col items-end gap-1.5">
                          {isVip && (
                            <span data-testid={`crash-full-${c.class_id}`} title={`All ${c.crash_cap ?? 4} VIP overflow spots are taken`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-400 border border-neutral-800 px-1.5 py-0.5">
                              <Crown size={10} aria-hidden="true" /> Crash cap full
                            </span>
                          )}
                          <button
                            onClick={() => joinWaitlist(c)}
                            data-testid={`waitlist-btn-${c.class_id}`}
                            className="text-[11px] uppercase tracking-[0.15em] border border-neon/50 text-neon px-4 py-2.5 hover:bg-neon hover:text-white transition-colors"
                          >
                            Waitlist
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() => openBooking(c)}
                        data-testid={`book-btn-${c.class_id}`}
                        className="shrink-0 text-[11px] uppercase tracking-[0.15em] border border-neutral-700 px-4 py-2.5 hover:border-neon hover:text-neon transition-colors"
                      >
                        Book
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <MatMapDialog cls={active} date={activeDate} onClose={() => setActive(null)} onChanged={() => { loadClasses(); loadFriends(); }} />
    </section>
  );
};
